import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartDiscountUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  get sectionId() {
    return this.dataset.sectionId || this.closest('.shopify-section')?.id?.replace('shopify-section-', '') || '';
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('shopify:cart:lines-update', this.#handleCartChange);
    const input = this.querySelector('input[name="discount"]');
    if (input) {
      input.addEventListener('input', this.#handleInputClear);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('shopify:cart:lines-update', this.#handleCartChange);
    const input = this.querySelector('input[name="discount"]');
    if (input) {
      input.removeEventListener('input', this.#handleInputClear);
    }
  }

  #handleInputClear = () => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    if (cartDiscountError) cartDiscountError.classList.add('hidden');
    if (cartDiscountErrorDiscountCode) cartDiscountErrorDiscountCode.classList.add('hidden');
    if (cartDiscountErrorShipping) cartDiscountErrorShipping.classList.add('hidden');
  };

  #handleCartChange = async (event) => {
    if (!event.detail || !event.detail.resource || !this.sectionId) return;
    const cart = event.detail.resource;
    const cartTotalCents = cart.original_total_price || (cart.items ? cart.items.reduce((sum, item) => sum + ((item.original_price || item.price) * item.quantity), 0) : cart.total_price);
    const existing = this.#existingDiscounts();

    const welcomeOfferAppliedInSession = cart.attributes && cart.attributes.welcome500_applied === 'true';

    if ((existing.includes('WELCOME500') || welcomeOfferAppliedInSession) && cartTotalCents < 249900) {
      const index = existing.indexOf('WELCOME500');
      if (index !== -1) {
        existing.splice(index, 1);
      }

      const abortController = this.#createAbortController();
      try {
        const config = fetchConfig('json', {
          body: JSON.stringify({
            discount: existing.join(','),
            attributes: { welcome500_applied: 'false' },
            sections: [this.sectionId],
          }),
        });

        this.dataset.welcomeOfferApplied = 'false';

        const response = await fetch(Theme.routes.cart_update_url, {
          ...config,
          signal: abortController.signal,
        });

        const data = await response.json();
        this.#syncCheckoutDiscount(existing);
        this.#handleDiscountError('discount_code', 'WELCOME500 coupon was removed because your order total is below ₹2,499.');

        morphSection(this.sectionId, data.sections[this.sectionId], {
          mode: this.closest('theme-drawer') ? 'hydration' : 'full',
        });
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
          console.error('[cart-discount] Auto-removal error:', error);
        }
      } finally {
        this.#activeFetch = null;
      }
    } else {
      if (existing.includes('WELCOME500')) {
        this.dataset.welcomeOfferApplied = 'true';
      } else {
        this.dataset.welcomeOfferApplied = 'false';
      }
    }
  };

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || !this.sectionId) return;

    const discountCodeValue = discountCode.value.trim().toUpperCase();
    discountCode.value = discountCodeValue;
    if (!discountCodeValue) return;

    /** @type {HTMLButtonElement | null} */
    const submitButton = form.querySelector('button[type="submit"]');
    const originalButtonHtml = submitButton ? submitButton.innerHTML : '';
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.innerHTML = '<span class="spinner-small"></span>';
    }

    const abortController = this.#createAbortController();

    const existingDiscounts = this.#existingDiscounts();
    const cleanVal = String(this.dataset.cartSubtotalCents || this.dataset.cartTotalCents || '0').replace(/\D/g, '');
    let cartTotalCents = cleanVal ? parseInt(cleanVal, 10) : 0;
    if (window.cart && window.cart.items) {
      cartTotalCents = window.cart.items.reduce((sum, item) => sum + ((item.original_price || item.price) * item.quantity), 0);
    }

    const isEligible = this.dataset.welcomeOfferEligible !== 'false';
    if (discountCodeValue === 'WELCOME500' && !isEligible) {
      this.#handleDiscountError('discount_code', 'WELCOME500 is only valid for first-time customers.');
      discountCode.value = '';
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.innerHTML = originalButtonHtml;
      }
      const quickApplyButton = this.querySelector(`[data-cart-discount-code="${discountCodeValue}"]`);
      if (quickApplyButton) {
        quickApplyButton.removeAttribute('disabled');
        quickApplyButton.innerHTML = 'Apply';
      }
      return;
    }

    if (discountCodeValue === 'WELCOME500' && cartTotalCents < 249900) {
      this.#handleDiscountError('discount_code', 'WELCOME500 is valid only on orders of ₹2,499 or above.');
      discountCode.value = '';
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.innerHTML = originalButtonHtml;
      }
      const quickApplyButton = this.querySelector(`[data-cart-discount-code="${discountCodeValue}"]`);
      if (quickApplyButton) {
        quickApplyButton.removeAttribute('disabled');
        quickApplyButton.innerHTML = 'Apply';
      }
      return;
    }

    if (discountCodeValue === 'WELCOME500' && existingDiscounts.some((code) => code.toUpperCase() === 'WELCOME500')) {
      // Already applied as a discount code, nothing to do
      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');
      discountCode.value = '';
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.innerHTML = originalButtonHtml;
      }
      return;
    }

    if (existingDiscounts.some((code) => code.toUpperCase() === discountCodeValue)) {
      this.#syncCheckoutDiscount(existingDiscounts);
      return;
    }

    cartDiscountError.classList.add('hidden');
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');

    const allDiscountCodes = [...existingDiscounts, discountCodeValue];
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: allDiscountCodes.map((code) => ({ code })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const payload = {
        discount: allDiscountCodes.join(','),
        sections: [this.sectionId],
      };
      if (allDiscountCodes.includes('WELCOME500')) {
        payload.attributes = { welcome500_applied: 'true' };
      } else {
        payload.attributes = { welcome500_applied: 'false' };
      }

      const config = fetchConfig('json', {
        body: JSON.stringify(payload),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      const isWelcome500Mock = discountCodeValue === 'WELCOME500' && cartTotalCents >= 249900;
      if (
        !isWelcome500Mock &&
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code', `${discountCodeValue} is not a valid coupon code.`);
        deferredPromise.resolve({
          cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        });
        return;
      }

      const newHtml = data.sections[this.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          deferredPromise.resolve({
            cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
          });
          return;
        }
      }

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });
      // Clear the input explicitly: data-skip-node-update on <input name="discount"> means
      // morphSection no longer syncs the input value from the server-rendered empty state,
      // so without this the user's typed code stays in the field after a successful apply.
      discountCode.value = '';
      this.#syncCheckoutDiscount([...existingDiscounts, discountCodeValue]);
      morphSection(this.sectionId, newHtml, { mode: this.closest('theme-drawer') ? 'hydration' : 'full' });
    } catch (error) {
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to apply discount',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#activeFetch = null;
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.innerHTML = originalButtonHtml;
      }
      const quickApplyButton = this.querySelector(`[data-cart-discount-code="${discountCodeValue}"]`);
      if (quickApplyButton) {
        quickApplyButton.removeAttribute('disabled');
        quickApplyButton.innerHTML = 'Apply';
      }
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      !(event.target instanceof Element) ||
      !this.sectionId
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: existingDiscounts.map((code) => ({ code })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const payload = {
        discount: existingDiscounts.join(','),
        sections: [this.sectionId],
      };
      if (!existingDiscounts.includes('WELCOME500')) {
        payload.attributes = { welcome500_applied: 'false' };
      }
      const config = fetchConfig('json', {
        body: JSON.stringify(payload),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });

      this.#syncCheckoutDiscount(existingDiscounts);
      const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      morphSection(this.sectionId, data.sections[this.sectionId], {
        mode: this.closest('theme-drawer') ? 'hydration' : 'full',
      });
    } catch (error) {
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to remove discount',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles showing or dispatching discount errors.
   * @param {'discount_code' | 'shipping'} type - The type of the discount error.
   * @param {string} [customMessage] - An optional custom error message.
   */
  #handleDiscountError(type, customMessage = '') {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    
    if (target) {
      if (customMessage) {
        target.textContent = customMessage;
      } else {
        if (type === 'discount_code') {
          target.textContent = 'Discount code cannot be applied to your cart';
        } else {
          target.textContent = 'Discount not applicable for shipping';
        }
      }
      target.classList.remove('hidden');
    }
    
    cartDiscountError.classList.remove('hidden');

    const errorMessage = customMessage || (type === 'discount_code' ? 'Invalid discount code' : 'Discount not applicable for shipping');
    this.dispatchEvent(
      new CartErrorEvent({
        error: errorMessage,
        code: 'VALIDATION_CUSTOM',
      })
    );
  }

  /**
   * Applies a suggested discount code from the cart UI.
   * @param {MouseEvent | KeyboardEvent} event
   */
  applySuggestedDiscount = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('[data-cart-discount-code]');
    if (!(button instanceof HTMLButtonElement)) return;

    const discountCodeValue = button.dataset.cartDiscountCode;
    const form = this.querySelector('.cart-discount__form');
    const discountCode = form?.querySelector('input[name="discount"]');

    if (!discountCodeValue || !(form instanceof HTMLFormElement) || !(discountCode instanceof HTMLInputElement)) return;

    discountCode.value = discountCodeValue;
    button.disabled = true;
    button.innerHTML = 'Applying...';
    
    const mockEvent = {
      preventDefault: () => {},
      stopPropagation: () => {},
      target: form
    };
    this.applyDiscount(mockEvent);
  };

  /**
   * Keeps the checkout form discount input aligned with applied cart discounts.
   * @param {string[]} discountCodes
   */
  #syncCheckoutDiscount(discountCodes) {
    const cartForm = document.getElementById('cart-form');
    if (!(cartForm instanceof HTMLFormElement)) return;

    /** @type {HTMLInputElement | null} */
    let discountInput = document.querySelector('[data-cart-checkout-discount]');
    if (!(discountInput instanceof HTMLInputElement)) {
      discountInput = document.createElement('input');
      discountInput.type = 'hidden';
      discountInput.name = 'discount';
      discountInput.dataset.cartCheckoutDiscount = '';
      cartForm.appendChild(discountInput);
    }

    discountInput.value = discountCodes.filter(Boolean).join(',');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
